import { defineDomainTranslations } from './defineDomainTranslations';

const en = {
  'createProject.additionalParameters': 'Additional parameters',
  'createProject.bulkUpload.contactSupport': 'contact support',
  'createProject.bulkUpload.errorPrefix': "Couldn't upload some files. Please try again or",
  'createProject.dictionary.add': 'Add Dictionary',
  'createProject.dictionary.choose': 'Choose dictionary',
  'createProject.dictionary.createNew': 'Create new',
  'createProject.dictionary.label': 'Translation dictionary',
  'createProject.dictionary.upgrade': 'Upgrade',
  'createProject.error.chooseOnlyOneFile': 'Choose only 1 file',
  'createProject.error.correctLink': 'Please provide a valid link',
  'createProject.error.fileTooLarge': 'File is too large',
  'createProject.error.fileTooLargeWithMax': 'File is too large. Max {maxSize} MB',
  'createProject.error.fileTooSmall': 'File is too small',
  'createProject.error.invalidFileType': 'Invalid file type',
  'createProject.error.maximumVideo': 'Maximum of {count} video',
  'createProject.error.maximumVideos': 'Maximum of {count} videos',
  'createProject.error.server': 'Server error',
  'createProject.error.somethingWentWrong': 'Something went wrong',
  'createProject.error.srtFormat': 'Invalid SRT file format. Choose another SRT file or continue without one.',
  'createProject.error.srtMismatch': 'Please ensure both SRT files have identical timestamps or remove one.',
  'createProject.error.unavailablePublicLink': "We couldn't add that link. Make sure it's a public video URL.",
  'createProject.error.unknown': 'Unknown error',
  'createProject.error.unsupportedFormat': "That format isn't supported. Use MP4, MOV, WEBM, MKV, MP3, or WAV.",
  'createProject.error.videoUploadFailed': 'Video upload failed',
  'createProject.language.aiPresets': 'Translation: {language}, voiceover: AI presets',
  'createProject.language.voiceClone': 'Translation: {language}, voiceover: AI presets or {voiceLanguage} voice clone',
  'createProject.limit.paid': 'File exceeds limit ({size} MB/{minutes} min). Please select another.',
  'createProject.limit.subtitle.durationMinutes': 'Maximum video duration: {duration} min.',
  'createProject.limit.subtitle.durationSeconds': 'Maximum video duration: {duration} sec.',
  'createProject.limit.subtitle.size': 'Maximum video size: {maxSize} MB.',
  'createProject.limit.subtitle.sizeOrDurationMinutes':
    'Maximum video size: {maxSize} MB or maximum video duration: {duration} min.',
  'createProject.limit.subtitle.sizeOrDurationSeconds':
    'Maximum video size: {maxSize} MB or maximum video duration: {duration} sec.',
  'createProject.limit.trial': 'File exceeds 1GB trial limit. Please select another.',
  'createProject.limit.upgrade': 'Upgrade to upload without limitations',
  'createProject.link.fallbackTitle': 'Video from link',
  'createProject.link.pendingName': 'File from link',
  'createProject.mobile.description':
    'To use this feature, please switch to a desktop device and view this page in full screen',
  'createProject.mobile.title': 'Desktop Required',
  'createProject.notAccess.contactPrefix': 'Please contact',
  'createProject.notAccess.contactSuffix': 'for further assistance',
  'createProject.notAccess.imageAlt': 'Access unavailable',
  'createProject.notAccess.title': 'You do not have permission to generate videos',
  'createProject.screenRecord.beta': 'Beta',
  'createProject.screenRecord.closeAria': 'Close',
  'createProject.screenRecord.downloadIconAlt': 'Download icon',
  'createProject.screenRecord.installButton': 'Install Extension',
  'createProject.screenRecord.installDescription':
    'To capture higher-quality videos and use the zoom feature, install our Chrome extension. This is a one-time setup and takes less than a minute.',
  'createProject.screenRecord.installTitle': 'Install Chrome Extension',
  'createProject.screenRecord.promoDescription': 'Record and get a polished video — free for now.',
  'createProject.screenRecord.promoModalTitle': 'Screen recording is here — and you get it first',
  'createProject.screenRecord.promoTitle': 'AI screen recording is in beta.',
  'createProject.screenRecord.smartDescription': 'Smart screen recorder for the best results',
  'createProject.screenRecord.startButton': 'Start recording',
  'createProject.screenRecord.tryButton': 'Try it',
  'createProject.srt.singleVideoTooltip':
    'SRT upload is available only when translating a single video into a single language',
  'createProject.support.contact': 'contact our support team',
  'createProject.support.problemPrefix': 'Something went wrong. Please try again or',
  'createProject.upload.error': 'Upload error',
  'createProject.upload.fileUploaded': 'File uploaded',
  'createProject.upload.finishing': 'Just a few moments left',
  'createProject.upload.inProgress': "Upload in progress — don't close this tab.",
  'createProject.upload.progress': '{percent}% uploaded',
  'createProject.upload.retrying': 'Retrying upload. Please wait.',
} as const;

type CreateProjectTranslation = Record<keyof typeof en, string>;

const es = {
  'createProject.additionalParameters': 'Parámetros adicionales',
  'createProject.bulkUpload.contactSupport': 'contacta con soporte',
  'createProject.bulkUpload.errorPrefix': 'No se han podido subir algunos archivos. Inténtalo de nuevo o',
  'createProject.dictionary.add': 'Añadir diccionario',
  'createProject.dictionary.choose': 'Elegir diccionario',
  'createProject.dictionary.createNew': 'Crear',
  'createProject.dictionary.label': 'Diccionario de traducción',
  'createProject.dictionary.upgrade': 'Mejorar plan',
  'createProject.error.chooseOnlyOneFile': 'Elige solo 1 archivo',
  'createProject.error.correctLink': 'Introduce un enlace válido',
  'createProject.error.fileTooLarge': 'El archivo es demasiado grande',
  'createProject.error.fileTooLargeWithMax': 'El archivo es demasiado grande. Máximo: {maxSize} MB',
  'createProject.error.fileTooSmall': 'El archivo es demasiado pequeño',
  'createProject.error.invalidFileType': 'Tipo de archivo no válido',
  'createProject.error.maximumVideo': 'Máximo: {count} video',
  'createProject.error.maximumVideos': 'Máximo: {count} videos',
  'createProject.error.server': 'Error del servidor',
  'createProject.error.somethingWentWrong': 'Algo salió mal',
  'createProject.error.srtFormat': 'El formato del archivo SRT no es válido. Usa otro archivo o no subas el SRT.',
  'createProject.error.srtMismatch':
    'Asegúrate de que ambos archivos SRT tengan marcas de tiempo idénticas o elimina uno.',
  'createProject.error.unavailablePublicLink':
    'No hemos podido añadir ese enlace. Asegúrate de que sea la URL pública de un video.',
  'createProject.error.unknown': 'Error desconocido',
  'createProject.error.unsupportedFormat': 'Ese formato no es compatible. Usa MP4, MOV, WEBM, MKV, MP3 o WAV.',
  'createProject.error.videoUploadFailed': 'No se ha podido subir el video',
  'createProject.language.aiPresets': 'Traducción: {language}; voz: voces de IA predefinidas',
  'createProject.language.voiceClone':
    'Traducción: {language}; voz: voces de IA predefinidas o clonación de voz en {voiceLanguage}',
  'createProject.limit.paid': 'El archivo supera el límite ({size} MB/{minutes} min). Elige otro.',
  'createProject.limit.subtitle.durationMinutes': 'Duración máxima del video: {duration} min.',
  'createProject.limit.subtitle.durationSeconds': 'Duración máxima del video: {duration} s.',
  'createProject.limit.subtitle.size': 'Tamaño máximo del video: {maxSize} MB.',
  'createProject.limit.subtitle.sizeOrDurationMinutes':
    'Tamaño máximo del video: {maxSize} MB o duración máxima: {duration} min.',
  'createProject.limit.subtitle.sizeOrDurationSeconds':
    'Tamaño máximo del video: {maxSize} MB o duración máxima: {duration} s.',
  'createProject.limit.trial': 'El archivo supera el límite de prueba de 1 GB. Elige otro.',
  'createProject.limit.upgrade': 'Mejora el plan para subir archivos sin limitaciones',
  'createProject.link.fallbackTitle': 'Video desde un enlace',
  'createProject.link.pendingName': 'Archivo desde un enlace',
  'createProject.mobile.description':
    'Para usar esta función, cambia a un ordenador y abre esta página a pantalla completa.',
  'createProject.mobile.title': '📱Aviso para móviles',
  'createProject.notAccess.contactPrefix': 'Ponte en contacto con',
  'createProject.notAccess.contactSuffix': 'para obtener ayuda',
  'createProject.notAccess.imageAlt': 'Acceso no disponible',
  'createProject.notAccess.title': 'No tienes acceso para generar videos',
  'createProject.screenRecord.beta': 'Beta',
  'createProject.screenRecord.closeAria': 'Cerrar',
  'createProject.screenRecord.downloadIconAlt': 'Icono de descarga',
  'createProject.screenRecord.installButton': 'Instalar extensión',
  'createProject.screenRecord.installDescription':
    'Para capturar videos de mayor calidad y usar el zoom, instala nuestra extensión de Chrome. Solo tienes que hacerlo una vez y tarda menos de un minuto.',
  'createProject.screenRecord.installTitle': 'Instala la extensión de Chrome',
  'createProject.screenRecord.promoDescription': 'Graba y obtén un video profesional — gratis por ahora.',
  'createProject.screenRecord.promoModalTitle': 'La grabación de pantalla ya está aquí — y tú la pruebas primero',
  'createProject.screenRecord.promoTitle': 'La grabación de pantalla con IA está en beta.',
  'createProject.screenRecord.smartDescription': 'Grabador de pantalla inteligente para obtener el mejor resultado',
  'createProject.screenRecord.startButton': 'Empezar a grabar',
  'createProject.screenRecord.tryButton': 'Probar',
  'createProject.srt.singleVideoTooltip':
    'Solo puedes subir archivos SRT al traducir un único video a un único idioma.',
  'createProject.support.contact': 'ponte en contacto con nuestro equipo de soporte',
  'createProject.support.problemPrefix': 'Algo salió mal. Inténtalo de nuevo o',
  'createProject.upload.error': 'Error de subida',
  'createProject.upload.fileUploaded': 'Archivo subido',
  'createProject.upload.finishing': 'Solo faltan unos instantes',
  'createProject.upload.inProgress': 'Carga en curso. No cierres esta pestaña.',
  'createProject.upload.progress': '{percent}% subido',
  'createProject.upload.retrying': 'Reintentando la carga. Espera un momento.',
} satisfies CreateProjectTranslation;

const pt = {
  'createProject.additionalParameters': 'Parâmetros adicionais',
  'createProject.bulkUpload.contactSupport': 'fale com o suporte',
  'createProject.bulkUpload.errorPrefix': 'Não foi possível enviar alguns arquivos. Tente novamente ou',
  'createProject.dictionary.add': 'Adicionar dicionário',
  'createProject.dictionary.choose': 'Escolher dicionário',
  'createProject.dictionary.createNew': 'Criar novo',
  'createProject.dictionary.label': 'Dicionário de tradução',
  'createProject.dictionary.upgrade': 'Fazer upgrade',
  'createProject.error.chooseOnlyOneFile': 'Escolha apenas 1 arquivo',
  'createProject.error.correctLink': 'Insira um link válido',
  'createProject.error.fileTooLarge': 'O arquivo é muito grande',
  'createProject.error.fileTooLargeWithMax': 'O arquivo é muito grande. Máximo: {maxSize} MB',
  'createProject.error.fileTooSmall': 'O arquivo é muito pequeno',
  'createProject.error.invalidFileType': 'Tipo de arquivo inválido',
  'createProject.error.maximumVideo': 'Máximo de {count} vídeo',
  'createProject.error.maximumVideos': 'Máximo de {count} vídeos',
  'createProject.error.server': 'Erro do servidor',
  'createProject.error.somethingWentWrong': 'Algo deu errado',
  'createProject.error.srtFormat': 'Formato de arquivo SRT incorreto. Use outro arquivo ou não envie o SRT.',
  'createProject.error.srtMismatch':
    'Verifique se os dois arquivos SRT têm marcações de tempo idênticas ou remova um deles.',
  'createProject.error.unavailablePublicLink':
    'Não foi possível adicionar esse link. Verifique se é uma URL pública de vídeo.',
  'createProject.error.unknown': 'Erro desconhecido',
  'createProject.error.unsupportedFormat': 'Esse formato não é compatível. Use MP4, MOV, WEBM, MKV, MP3 ou WAV.',
  'createProject.error.videoUploadFailed': 'Falha no envio do vídeo',
  'createProject.language.aiPresets': 'Tradução: {language}; voz: vozes de IA predefinidas',
  'createProject.language.voiceClone':
    'Tradução: {language}; voz: vozes de IA predefinidas ou clonagem de voz em {voiceLanguage}',
  'createProject.limit.paid': 'O arquivo excede o limite ({size} MB/{minutes} min). Selecione outro.',
  'createProject.limit.subtitle.durationMinutes': 'Duração máxima do vídeo: {duration} min.',
  'createProject.limit.subtitle.durationSeconds': 'Duração máxima do vídeo: {duration} s.',
  'createProject.limit.subtitle.size': 'Tamanho máximo do vídeo: {maxSize} MB.',
  'createProject.limit.subtitle.sizeOrDurationMinutes':
    'Tamanho máximo do vídeo: {maxSize} MB ou duração máxima: {duration} min.',
  'createProject.limit.subtitle.sizeOrDurationSeconds':
    'Tamanho máximo do vídeo: {maxSize} MB ou duração máxima: {duration} s.',
  'createProject.limit.trial': 'O arquivo excede o limite de teste de 1 GB. Selecione outro.',
  'createProject.limit.upgrade': 'Faça upgrade do plano para enviar arquivos sem limitações',
  'createProject.link.fallbackTitle': 'Vídeo de um link',
  'createProject.link.pendingName': 'Arquivo de um link',
  'createProject.mobile.description':
    'Para usar este recurso, mude para um computador e abra esta página em tela cheia.',
  'createProject.mobile.title': '📱Aviso para dispositivos móveis',
  'createProject.notAccess.contactPrefix': 'Entre em contato com',
  'createProject.notAccess.contactSuffix': 'para obter ajuda',
  'createProject.notAccess.imageAlt': 'Acesso indisponível',
  'createProject.notAccess.title': 'Você não tem acesso para gerar vídeos',
  'createProject.screenRecord.beta': 'Beta',
  'createProject.screenRecord.closeAria': 'Fechar',
  'createProject.screenRecord.downloadIconAlt': 'Ícone de download',
  'createProject.screenRecord.installButton': 'Instalar extensão',
  'createProject.screenRecord.installDescription':
    'Para capturar vídeos com mais qualidade e usar o zoom, instale nossa extensão do Chrome. Esta configuração é feita uma única vez e leva menos de um minuto.',
  'createProject.screenRecord.installTitle': 'Instale a extensão do Chrome',
  'createProject.screenRecord.promoDescription':
    'Grave e obtenha um vídeo com acabamento profissional — grátis por enquanto.',
  'createProject.screenRecord.promoModalTitle': 'A gravação de tela chegou — e você é um dos primeiros a testar',
  'createProject.screenRecord.promoTitle': 'A gravação de tela com IA está em beta.',
  'createProject.screenRecord.smartDescription': 'Gravador de tela inteligente para melhores resultados',
  'createProject.screenRecord.startButton': 'Iniciar gravação',
  'createProject.screenRecord.tryButton': 'Testar',
  'createProject.srt.singleVideoTooltip':
    'O envio de SRT está disponível apenas ao traduzir um único vídeo para um único idioma.',
  'createProject.support.contact': 'fale com nossa equipe de suporte',
  'createProject.support.problemPrefix': 'Algo deu errado. Tente novamente ou',
  'createProject.upload.error': 'Erro no envio',
  'createProject.upload.fileUploaded': 'Arquivo enviado',
  'createProject.upload.finishing': 'Faltam só alguns instantes',
  'createProject.upload.inProgress': 'Envio em andamento. Não feche esta aba.',
  'createProject.upload.progress': '{percent}% enviado',
  'createProject.upload.retrying': 'Tentando enviar novamente. Aguarde.',
} satisfies CreateProjectTranslation;

const fr = {
  'createProject.additionalParameters': 'Paramètres supplémentaires',
  'createProject.bulkUpload.contactSupport': "contactez l'assistance",
  'createProject.bulkUpload.errorPrefix': "Impossible d'importer certains fichiers. Réessayez ou",
  'createProject.dictionary.add': 'Ajouter un dictionnaire',
  'createProject.dictionary.choose': 'Choisir un dictionnaire',
  'createProject.dictionary.createNew': 'Créer',
  'createProject.dictionary.label': 'Dictionnaire de traduction',
  'createProject.dictionary.upgrade': "Changer d'offre",
  'createProject.error.chooseOnlyOneFile': 'Choisissez un seul fichier',
  'createProject.error.correctLink': 'Saisissez un lien valide',
  'createProject.error.fileTooLarge': 'Le fichier est trop volumineux',
  'createProject.error.fileTooLargeWithMax': 'Le fichier est trop volumineux. Maximum : {maxSize} Mo',
  'createProject.error.fileTooSmall': 'Le fichier est trop petit',
  'createProject.error.invalidFileType': 'Type de fichier non valide',
  'createProject.error.maximumVideo': '{count} vidéo maximum',
  'createProject.error.maximumVideos': '{count} vidéos maximum',
  'createProject.error.server': 'Erreur du serveur',
  'createProject.error.somethingWentWrong': "Une erreur s'est produite",
  'createProject.error.srtFormat':
    "Format de fichier SRT incorrect. Utilisez un autre fichier ou n'importez pas de SRT.",
  'createProject.error.srtMismatch':
    'Vérifiez que les deux fichiers SRT ont des horodatages identiques ou supprimez-en un.',
  'createProject.error.unavailablePublicLink':
    "Impossible d'ajouter ce lien. Vérifiez qu'il s'agit de l'URL publique d'une vidéo.",
  'createProject.error.unknown': 'Erreur inconnue',
  'createProject.error.unsupportedFormat':
    "Ce format n'est pas pris en charge. Utilisez MP4, MOV, WEBM, MKV, MP3 ou WAV.",
  'createProject.error.videoUploadFailed': "Échec de l'importation de la vidéo",
  'createProject.language.aiPresets': 'Traduction : {language} ; voix off : préréglages IA',
  'createProject.language.voiceClone':
    'Traduction : {language} ; voix off : préréglages IA ou clonage vocal en {voiceLanguage}',
  'createProject.limit.paid': 'Le fichier dépasse la limite ({size} Mo/{minutes} min). Choisissez-en un autre.',
  'createProject.limit.subtitle.durationMinutes': 'Durée maximale de la vidéo : {duration} min.',
  'createProject.limit.subtitle.durationSeconds': 'Durée maximale de la vidéo : {duration} s.',
  'createProject.limit.subtitle.size': 'Taille maximale de la vidéo : {maxSize} Mo.',
  'createProject.limit.subtitle.sizeOrDurationMinutes':
    'Taille maximale de la vidéo : {maxSize} Mo ou durée maximale : {duration} min.',
  'createProject.limit.subtitle.sizeOrDurationSeconds':
    'Taille maximale de la vidéo : {maxSize} Mo ou durée maximale : {duration} s.',
  'createProject.limit.trial': "Le fichier dépasse la limite d'essai de 1 Go. Choisissez-en un autre.",
  'createProject.limit.upgrade': "Changez d'offre pour importer des fichiers sans limites",
  'createProject.link.fallbackTitle': "Vidéo provenant d'un lien",
  'createProject.link.pendingName': "Fichier provenant d'un lien",
  'createProject.mobile.description':
    'Pour utiliser cette fonctionnalité, passez sur un ordinateur et affichez cette page en plein écran.',
  'createProject.mobile.title': 'Ordinateur requis',
  'createProject.notAccess.contactPrefix': 'Contactez',
  'createProject.notAccess.contactSuffix': "pour obtenir de l'aide",
  'createProject.notAccess.imageAlt': 'Accès indisponible',
  'createProject.notAccess.title': "Vous n'avez pas accès à la génération de vidéos",
  'createProject.screenRecord.beta': 'Bêta',
  'createProject.screenRecord.closeAria': 'Fermer',
  'createProject.screenRecord.downloadIconAlt': 'Icône de téléchargement',
  'createProject.screenRecord.installButton': "Installer l'extension",
  'createProject.screenRecord.installDescription':
    "Pour enregistrer des vidéos de meilleure qualité et utiliser le zoom, installez notre extension Chrome. Cette configuration unique prend moins d'une minute.",
  'createProject.screenRecord.installTitle': "Installer l'extension Chrome",
  'createProject.screenRecord.promoDescription': 'Enregistrez et obtenez une vidéo soignée — gratuite pour le moment.',
  'createProject.screenRecord.promoModalTitle':
    "L'enregistrement d'écran est disponible — et vous en profitez en premier",
  'createProject.screenRecord.promoTitle': "L'enregistrement d'écran par IA est en version bêta.",
  'createProject.screenRecord.smartDescription': "Enregistreur d'écran intelligent pour un résultat optimal",
  'createProject.screenRecord.startButton': "Démarrer l'enregistrement",
  'createProject.screenRecord.tryButton': 'Essayer',
  'createProject.srt.singleVideoTooltip':
    "L'importation d'un SRT est disponible uniquement pour traduire une seule vidéo dans une seule langue.",
  'createProject.support.contact': "contactez notre équipe d'assistance",
  'createProject.support.problemPrefix': "Une erreur s'est produite. Réessayez ou",
  'createProject.upload.error': "Erreur d'importation",
  'createProject.upload.fileUploaded': 'Fichier importé',
  'createProject.upload.finishing': 'Encore quelques instants',
  'createProject.upload.inProgress': 'Importation en cours. Ne fermez pas cet onglet.',
  'createProject.upload.progress': '{percent}% importé',
  'createProject.upload.retrying': "Nouvelle tentative d'importation. Veuillez patienter.",
} satisfies CreateProjectTranslation;

const it = {
  'createProject.additionalParameters': 'Parametri aggiuntivi',
  'createProject.bulkUpload.contactSupport': "contatta l'assistenza",
  'createProject.bulkUpload.errorPrefix': 'Impossibile caricare alcuni file. Riprova oppure',
  'createProject.dictionary.add': 'Aggiungi dizionario',
  'createProject.dictionary.choose': 'Scegli un dizionario',
  'createProject.dictionary.createNew': 'Crea un nuovo dizionario',
  'createProject.dictionary.label': 'Dizionario di traduzione',
  'createProject.dictionary.upgrade': 'Passa a un piano superiore',
  'createProject.error.chooseOnlyOneFile': 'Scegli un solo file',
  'createProject.error.correctLink': 'Inserisci un link valido',
  'createProject.error.fileTooLarge': 'Il file è troppo grande',
  'createProject.error.fileTooLargeWithMax': 'Il file è troppo grande. Massimo: {maxSize} MB',
  'createProject.error.fileTooSmall': 'Il file è troppo piccolo',
  'createProject.error.invalidFileType': 'Tipo di file non valido',
  'createProject.error.maximumVideo': 'Massimo {count} video',
  'createProject.error.maximumVideos': 'Massimo {count} video',
  'createProject.error.server': 'Errore del server',
  'createProject.error.somethingWentWrong': 'Qualcosa è andato storto',
  'createProject.error.srtFormat': 'Formato del file SRT errato. Usa un altro file o non caricare il file SRT.',
  'createProject.error.srtMismatch':
    'Assicurati che entrambi i file SRT abbiano timestamp identici oppure rimuovine uno.',
  'createProject.error.unavailablePublicLink':
    'Non è stato possibile aggiungere il link. Assicurati che sia un URL pubblico di un video.',
  'createProject.error.unknown': 'Errore sconosciuto',
  'createProject.error.unsupportedFormat': 'Questo formato non è supportato. Usa MP4, MOV, WEBM, MKV, MP3 o WAV.',
  'createProject.error.videoUploadFailed': 'Caricamento del video non riuscito',
  'createProject.language.aiPresets': 'Traduzione: {language}; voce: voci IA predefinite',
  'createProject.language.voiceClone':
    'Traduzione: {language}; voce: voci IA predefinite o clonazione vocale in {voiceLanguage}',
  'createProject.limit.paid': 'Il file supera il limite ({size} MB/{minutes} min). Selezionane un altro.',
  'createProject.limit.subtitle.durationMinutes': 'Durata massima del video: {duration} min.',
  'createProject.limit.subtitle.durationSeconds': 'Durata massima del video: {duration} sec.',
  'createProject.limit.subtitle.size': 'Dimensione massima del video: {maxSize} MB.',
  'createProject.limit.subtitle.sizeOrDurationMinutes':
    'Dimensione massima del video: {maxSize} MB o durata massima: {duration} min.',
  'createProject.limit.subtitle.sizeOrDurationSeconds':
    'Dimensione massima del video: {maxSize} MB o durata massima: {duration} sec.',
  'createProject.limit.trial': 'Il file supera il limite di prova di 1 GB. Selezionane un altro.',
  'createProject.limit.upgrade': 'Passa a un piano superiore per caricare file senza limitazioni',
  'createProject.link.fallbackTitle': 'Video da un link',
  'createProject.link.pendingName': 'File da un link',
  'createProject.mobile.description':
    'Per utilizzare questa funzione, passa a un computer e visualizza la pagina a schermo intero.',
  'createProject.mobile.title': '📱Avviso per dispositivi mobili',
  'createProject.notAccess.contactPrefix': 'Contatta',
  'createProject.notAccess.contactSuffix': 'per ricevere assistenza',
  'createProject.notAccess.imageAlt': 'Accesso non disponibile',
  'createProject.notAccess.title': 'Non hai accesso alla generazione di video',
  'createProject.screenRecord.beta': 'Beta',
  'createProject.screenRecord.closeAria': 'Chiudi',
  'createProject.screenRecord.downloadIconAlt': 'Icona di download',
  'createProject.screenRecord.installButton': "Installa l'estensione",
  'createProject.screenRecord.installDescription':
    'Per acquisire video di qualità superiore e usare lo zoom, installa la nostra estensione Chrome. La configurazione va eseguita una sola volta e richiede meno di un minuto.',
  'createProject.screenRecord.installTitle': "Installa l'estensione Chrome",
  'createProject.screenRecord.promoDescription': 'Registra e ottieni un video curato — gratis per ora.',
  'createProject.screenRecord.promoModalTitle':
    'La registrazione dello schermo è arrivata — e sei tra le prime persone a provarla',
  'createProject.screenRecord.promoTitle': 'La registrazione dello schermo con IA è in beta.',
  'createProject.screenRecord.smartDescription':
    'Registratore dello schermo intelligente per ottenere risultati migliori',
  'createProject.screenRecord.startButton': 'Avvia registrazione',
  'createProject.screenRecord.tryButton': 'Prova',
  'createProject.srt.singleVideoTooltip':
    'Il caricamento di file SRT è disponibile solo per tradurre un singolo video in una sola lingua.',
  'createProject.support.contact': 'contatta il nostro team di assistenza',
  'createProject.support.problemPrefix': 'Qualcosa è andato storto. Riprova oppure',
  'createProject.upload.error': 'Errore di caricamento',
  'createProject.upload.fileUploaded': 'File caricato',
  'createProject.upload.finishing': 'Mancano solo pochi istanti',
  'createProject.upload.inProgress': 'Caricamento in corso. Non chiudere questa scheda.',
  'createProject.upload.progress': '{percent}% caricato',
  'createProject.upload.retrying': 'Nuovo tentativo di caricamento. Attendi.',
} satisfies CreateProjectTranslation;

const ru = {
  'createProject.additionalParameters': 'Дополнительные параметры',
  'createProject.bulkUpload.contactSupport': 'обратитесь в поддержку',
  'createProject.bulkUpload.errorPrefix': 'Не удалось загрузить некоторые файлы. Повторите попытку или',
  'createProject.dictionary.add': 'Добавить словарь',
  'createProject.dictionary.choose': 'Выбрать словарь',
  'createProject.dictionary.createNew': 'Создать',
  'createProject.dictionary.label': 'Словарь переводов',
  'createProject.dictionary.upgrade': 'Перейти на тариф выше',
  'createProject.error.chooseOnlyOneFile': 'Выберите только 1 файл',
  'createProject.error.correctLink': 'Укажите корректную ссылку',
  'createProject.error.fileTooLarge': 'Файл слишком большой',
  'createProject.error.fileTooLargeWithMax': 'Файл слишком большой. Максимум: {maxSize} МБ',
  'createProject.error.fileTooSmall': 'Файл слишком маленький',
  'createProject.error.invalidFileType': 'Недопустимый тип файла',
  'createProject.error.maximumVideo': 'Не более {count} видео',
  'createProject.error.maximumVideos': 'Не более {count} видео',
  'createProject.error.server': 'Ошибка сервера',
  'createProject.error.somethingWentWrong': 'Что-то пошло не так',
  'createProject.error.srtFormat': 'Неверный формат SRT. Выберите другой файл или не загружайте SRT.',
  'createProject.error.srtMismatch': 'Убедитесь, что таймкоды в обоих SRT совпадают, или удалите один из файлов.',
  'createProject.error.unavailablePublicLink':
    'Не удалось добавить ссылку. Убедитесь, что это общедоступная ссылка на видео.',
  'createProject.error.unknown': 'Неизвестная ошибка',
  'createProject.error.unsupportedFormat':
    'Этот формат не поддерживается. Используйте MP4, MOV, WEBM, MKV, MP3 или WAV.',
  'createProject.error.videoUploadFailed': 'Не удалось загрузить видео',
  'createProject.language.aiPresets': 'Перевод: {language}; озвучка: готовые ИИ-голоса',
  'createProject.language.voiceClone':
    'Перевод: {language}; озвучка: готовые ИИ-голоса или клонирование голоса ({voiceLanguage})',
  'createProject.limit.paid': 'Файл превышает лимит ({size} МБ/{minutes} мин). Выберите другой.',
  'createProject.limit.subtitle.durationMinutes': 'Максимальная длительность видео: {duration} мин.',
  'createProject.limit.subtitle.durationSeconds': 'Максимальная длительность видео: {duration} сек.',
  'createProject.limit.subtitle.size': 'Максимальный размер видео: {maxSize} МБ.',
  'createProject.limit.subtitle.sizeOrDurationMinutes':
    'Максимальный размер видео: {maxSize} МБ или максимальная длительность: {duration} мин.',
  'createProject.limit.subtitle.sizeOrDurationSeconds':
    'Максимальный размер видео: {maxSize} МБ или максимальная длительность: {duration} сек.',
  'createProject.limit.trial': 'Файл превышает пробный лимит 1 ГБ. Выберите другой.',
  'createProject.limit.upgrade': 'Улучшите тариф, чтобы загружать файлы без ограничений',
  'createProject.link.fallbackTitle': 'Видео по ссылке',
  'createProject.link.pendingName': 'Файл по ссылке',
  'createProject.mobile.description':
    'Чтобы использовать эту функцию, откройте страницу на компьютере в полноэкранном режиме.',
  'createProject.mobile.title': 'Доступно на компьютере',
  'createProject.notAccess.contactPrefix': 'Обратитесь по адресу',
  'createProject.notAccess.contactSuffix': 'за дополнительной помощью',
  'createProject.notAccess.imageAlt': 'Доступ отсутствует',
  'createProject.notAccess.title': 'У вас нет доступа к созданию видео',
  'createProject.screenRecord.beta': 'Бета',
  'createProject.screenRecord.closeAria': 'Закрыть',
  'createProject.screenRecord.downloadIconAlt': 'Значок скачивания',
  'createProject.screenRecord.installButton': 'Установить расширение',
  'createProject.screenRecord.installDescription':
    'Установите наше расширение Chrome, чтобы записывать видео в более высоком качестве и использовать приближение. Это одноразовая настройка, которая займёт меньше минуты.',
  'createProject.screenRecord.installTitle': 'Установите расширение Chrome',
  'createProject.screenRecord.promoDescription': 'Запишите экран и получите готовое видео — пока бесплатно.',
  'createProject.screenRecord.promoModalTitle': 'Запись экрана уже доступна — и вы попробуете её первыми',
  'createProject.screenRecord.promoTitle': 'Запись экрана с ИИ уже в бета-версии.',
  'createProject.screenRecord.smartDescription': 'Умная запись экрана для лучшего результата',
  'createProject.screenRecord.startButton': 'Начать запись',
  'createProject.screenRecord.tryButton': 'Попробовать',
  'createProject.srt.singleVideoTooltip': 'SRT можно загрузить только при переводе одного видео на один язык.',
  'createProject.support.contact': 'обратитесь в службу поддержки',
  'createProject.support.problemPrefix': 'Что-то пошло не так. Повторите попытку или',
  'createProject.upload.error': 'Ошибка загрузки',
  'createProject.upload.fileUploaded': 'Файл загружен',
  'createProject.upload.finishing': 'Осталось ещё немного',
  'createProject.upload.inProgress': 'Идёт загрузка. Не закрывайте эту вкладку.',
  'createProject.upload.progress': 'Загружено: {percent}%',
  'createProject.upload.retrying': 'Повторяем загрузку. Подождите.',
} satisfies CreateProjectTranslation;

export const CREATE_PROJECT_TRANSLATIONS = defineDomainTranslations({ en, es, fr, it, pt, ru });
